import { useCallback, useState } from 'react';
import { AppRoutes } from '../../../constants';
import { useAppDispatch, useAppSelector, useHandleApiResponse } from '../../../hooks';
import { closeZeusModal, setIsImpersonating, setOrganization, setUser } from '../../../redux/reducers';
import { useGetImpersonatedUserMutation } from '../../../services';
import { ImpersonatedUserData } from '../../../types';
import { ButtonGroup, TextButton } from '../../shared';

const IMPERSONATE_USER_ERROR_MSG = 'Failed to impersonate user';

interface ZeusModalActionsProps {
  selectedOrgId?: string;
  selectedUserId?: string;
}

const ZeusModalActions = ({ selectedOrgId, selectedUserId }: ZeusModalActionsProps) => {
  const [isImpersonateLoading, setIsImpersonateLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { user, organization, isImpersonating } = useAppSelector((state) => state.auth);

  // Check if the selected user is the same as the current user - used to disable the impersonate button.
  // We check both the orgId and userId in case the user is a part of multiple organizations.
  const isSameUser = selectedUserId === user?.id && selectedOrgId === organization?.id;
  const isImpersonateDisabled = isSameUser || !selectedOrgId || !selectedUserId;

  const handleApiResponse = useHandleApiResponse();
  const [getImpersonatedUser] = useGetImpersonatedUserMutation();

  // Manually refreshes the page.
  const reloadPage = useCallback(() => {
    setTimeout(() => {
      // Go to the main simulations page in case the user being impersonated does not have access to the current page.
      window.location.href = AppRoutes.SIMULATIONS;
    }, 100); // Ensure the Redux state is updated before reloading to avoid race condition.
  }, []);

  const onSuccess = useCallback(
    (data: ImpersonatedUserData) => {
      // Set the isImpersonating flag to true.
      dispatch(setIsImpersonating(true));
      // Set the organization and user in Redux.
      dispatch(setOrganization(data.organization));
      dispatch(setUser(data));
      // Close the Zeus modal and reload the page to re-run all queries and mutations.
      dispatch(closeZeusModal());
      reloadPage();
    },
    [dispatch, reloadPage]
  );

  // Starts impersonation.
  const handleImpersonateUser = useCallback(async () => {
    if (isImpersonateDisabled) return;
    setIsImpersonateLoading(true);
    try {
      // Fetch the impersonated user data.
      const response = await getImpersonatedUser({ orgId: selectedOrgId, impersonateUserId: selectedUserId });
      handleApiResponse({
        response,
        errorMsg: IMPERSONATE_USER_ERROR_MSG,
        onError: () => setIsImpersonateLoading(false),
        onSuccess,
      });
    } catch (error) {
      console.error(IMPERSONATE_USER_ERROR_MSG, error);
      setIsImpersonateLoading(false);
    }
  }, [isImpersonateDisabled, selectedOrgId, selectedUserId, handleApiResponse, onSuccess]);

  // Closes the modal and exits impersonation if impersonation is active.
  const handleCancel = useCallback(() => {
    dispatch(closeZeusModal());
    // Exit impersonation and reload the page to re-run all queries and mutations.
    if (isImpersonating) {
      dispatch(setIsImpersonating(false));
      reloadPage();
    }
  }, [dispatch, isImpersonating, reloadPage]);

  return (
    <ButtonGroup>
      <TextButton
        text="Impersonate user"
        onClick={handleImpersonateUser}
        disabled={isImpersonateDisabled}
        loading={isImpersonateLoading}
      />
      <TextButton text={isImpersonating ? 'Exit Zeus mode' : 'Cancel'} onClick={handleCancel} />
    </ButtonGroup>
  );
};

export default ZeusModalActions;
